if (!customElements.get('quick-add-to-bag')) {
  /**
   * Quick Add to Bag Web Component
   *
   * Handles the quick add-to-bag functionality for product cards, allowing users
   * to select product options and add items to cart without navigating to the
   * product detail page. Activates on hover and dynamically loads product options.
   *
   * @class QuickAddToBag
   * @extends HTMLElement
   */
  customElements.define('quick-add-to-bag', class QuickAddToBag extends HTMLElement {
    /**
     * Static registry of all active QuickAddToBag instances
     * Used to close other instances when one opens
     * @static
     * @type {Set<QuickAddToBag>}
     */
    static activeInstances = new Set();

    /**
     * Creates an instance of QuickAddToBag
     * @constructor
     */
    constructor() {
      super();
    }

    /**
     * Checks if debug mode is enabled for this component
     * @returns {boolean} True if debug class is present, false otherwise
     */
    allowDebug(){
      return this.classList.contains('debug');
    }

    /**
     * Called when the element is inserted into the DOM
     * Automatically initializes the component
     */
    connectedCallback(){
      this.init();
    }

    /**
     * Initializes the component by setting up elements and event listeners
     * Sets the initial state to inactive
     */
    init(){
      this.componentActive = false;
      this.setElements();
      this.setEventListeners();
    }

    /**
     * Caches DOM element references for efficient access
     * Sets up references to key elements within the component and parent card
     */
    setElements(){
      this.elements = {};
      this.elements.addToBagButton = this.querySelector(`button.quick-add-to-bag__trigger`);
      this.elements.cartMessages = this.querySelector('.quick-add-to-bag__cart-messages');
      this.elements.optionSelectContainer = this.querySelector(`.quick-add-to-bag__option-select-container`);
      this.elements.parentCard = this.closest('product-card');
    }

    /**
     * Sets up event listeners for mouse hover interactions
     * Uses mouseenter/mouseleave to activate/deactivate the component
     * Events are bound to the parent product card for better separation of concerns
     */
    setEventListeners(){
      // establish events on the parent product card, to keep the code a little more separated from the product card code
      // use mouseenter to reduce event triggering over and over.
      this.elements.parentCard?.addEventListener('mouseenter', async (event) => {
        await this.open();
      });

      this.elements.parentCard?.addEventListener('mouseleave', (event) => {
        this.close();
      });
    }

    /**
     * Closes all other QuickAddToBag instances
     * Ensures only one quick-add panel is open at a time
     * @static
     */
    static closeAllOthers(exceptInstance = null) {
      QuickAddToBag.activeInstances.forEach(instance => {
        if (instance !== exceptInstance && instance.isActive()) {
          instance.close();
        }
      });
    }

    /**
     * Opens the quick add to bag interface
     * Closes all other instances first, then injects product options and activates
     * @async
     * @returns {Promise<void>}
     */
    async open(){
      if(!this.elements.parentCard) return;

      // Close all other quick-add instances before opening this one
      QuickAddToBag.closeAllOthers(this);

      const successfulInjection = await this.injectProductOptions();
      if(successfulInjection){
        this.classList.add('active');
        this.componentActive = true;
        // Register this instance as active
        QuickAddToBag.activeInstances.add(this);
      }
    }

    /**
     * Closes the quick add to bag interface
     * Removes active state and deactivates the component
     */
    close(){
      this.classList.remove('active');
      this.componentActive = false;
      // Remove from active instances registry
      QuickAddToBag.activeInstances.delete(this);
    }

    /**
     * Checks if the component is currently active
     * @returns {boolean} True if component is active, false otherwise
     */
    isActive(){
      return this.componentActive;
    }

    /**
     * Clears any cart messages displayed to the user
     * Removes all content from the cart messages container
     */
    clearCartMessage(){
      if(!this.elements.cartMessages) return;
      this.elements.cartMessages.innerHTML = "";
    }

    /**
     * Displays a cart message to the user
     * @param {string} message - The message to display (typically an error message)
     */
    setCartMessage(message){
      if(!message || message.trim().length == 0) return;
      if(!this.elements.cartMessages) return;
      this.elements.cartMessages.innerHTML = `<p class="quick-add-to-bag__cart-error">${message}</p>`;
    }

    /**
     * Injects product options into the quick add to bag interface
     * Gets selected swatch data and fetches corresponding product options
     * @async
     * @returns {Promise<boolean>} True if injection was successful, false otherwise
     */
    async injectProductOptions(){
      const selectedSwatchData = this.elements.parentCard.getSelectedSwatchData();
      if(Object.keys(selectedSwatchData).length < 1) return false;
      try {
        if(this.allowDebug()){
          console.log(`Injecting product options for ${this.elements.parentCard.product.title}`, this, this.elements.parentCard, selectedSwatchData);
        }
        await Promise.all([
          this.getProductOptionData(selectedSwatchData),
        ]);
        return true;
      } catch(error){
        console.error(error);
      }
      return false;
    }

    /**
     * Retrieves and processes product option data for the selected variant
     * @async
     * @param {Object} selectedSwatchData - Data about the selected color swatch
     * @param {string} selectedSwatchData.url - URL to fetch product option data
     * @returns {Promise<boolean>} True if data was retrieved and processed, false otherwise
     */
    async getProductOptionData(selectedSwatchData) {
      if(!selectedSwatchData) return false;
      const variantData = await this.fetchProductOptionData(selectedSwatchData.url);
      if(variantData){
        if(this.allowDebug()){
          console.log(`Product options HTML pulled for ${this.elements.parentCard.product.title}`, variantData);
        }
        const html = new DOMParser().parseFromString(variantData, 'text/html');
        this.updateProductOptions(html.querySelector('.template-container'));
        return Promise.resolve();
      }
      if(this.allowDebug()){
        console.log(`No product options injected for ${this.elements.parentCard.product.title}`, this, this.elements.parentCard, selectedSwatchData);
      }
      return false;
    }

    /**
     * Fetches product option HTML from the server
     * @async
     * @param {string} productFetchUrl - Base URL for the product
     * @returns {Promise<string|boolean>} HTML string if successful, false otherwise
     */
    async fetchProductOptionData(productFetchUrl) {
      if(!productFetchUrl) return false;
      try{
        const response = await fetch(`${productFetchUrl}&view=product-card-add-to-bag-options`);
        const responseHTML = await response.text();
        return responseHTML;
      } catch (error) {
        console.error(error);
      }
      return false;
    }

    /**
     * Updates the product options HTML content
     * Does NOT preserve selections - options start fresh each time to prevent
     * auto add-to-cart from triggering unexpectedly on swatch changes
     * @param {HTMLElement} html - The new HTML content to inject
     */
    updateProductOptions(html){
      if(!html) return;

      // Replace the HTML content - selections are NOT preserved
      // This prevents auto add-to-cart from triggering on swatch changes
      this.elements.optionSelectContainer.innerHTML = html.innerHTML;

      this.updateProductOptionsAvailability();
    }

    /**
     * Updates the availability state of all product options
     * Handles complex logic for determining which options are available,
     * unavailable, or impossible combinations based on variant data
     * Filters variants by selected color and manages option states accordingly
     */
    updateProductOptionsAvailability() {
      if(this.allowDebug()){
        console.log(`Updating product options availability for ${this.elements.parentCard.product.title}`, this, this.elements.parentCard);
      }
      this.clearCartMessage();
      const cardVariants = this.elements.parentCard.getVariants();
      if(this.allowDebug()){
        console.log(`Card variants for ${this.elements.parentCard.product.title}`, cardVariants);
      }
      const optionGroups = this.elements.parentCard.querySelectorAll('.product-card__option');

      // Get selected color information for filtering variants
      const selectedSwatchData = this.elements.parentCard.getSelectedSwatchData();
      const colorOptionKey = this.elements.parentCard.product.color_option_key;
      const selectedColor = selectedSwatchData?.color;

      // Filter variants by selected color if we have one
      let filteredVariants = cardVariants;
      if (selectedColor && colorOptionKey) {
        filteredVariants = cardVariants.filter(variant =>
          variant[colorOptionKey] === selectedColor
        );
        if(this.allowDebug()){
          console.log(`Filtered variants for color "${selectedColor}":`, filteredVariants);
        }
      }

      // Store the current selection for each option level in key-value pair
      let currentSelection = {};
      optionGroups.forEach((group) => {
        const selectedRadio = group.querySelector('input:checked');
        if (selectedRadio) {
          currentSelection[group.dataset.optionKey] = selectedRadio.value;
        }
      });

      optionGroups.forEach((group) => {
        const radios = group.querySelectorAll('input[type="radio"]');
        const isColorOption = group.classList.contains('product-card-swatches');

        radios.forEach((radio) => {
          let isAvailable;
          let isImpossibleCombination = false;

          const tempSelection = { ...currentSelection, [group.dataset.optionKey]: radio.value };

          if (isColorOption) {
            // For color options, check availability across all variants (not filtered)
            isImpossibleCombination = !cardVariants.some(variant =>
              Object.keys(tempSelection).every(key => variant[key] === tempSelection[key])
            );

            isAvailable = cardVariants.some(variant =>
              variant[group.dataset.optionKey] === radio.value && variant.available_shopify
            );
          } else {
            // For non-color options, use filtered variants for more efficient checking
            const variantsToCheck = selectedColor && colorOptionKey ? filteredVariants : cardVariants;

            isImpossibleCombination = !variantsToCheck.some(variant =>
              Object.keys(tempSelection).every(key => variant[key] === tempSelection[key])
            );

            isAvailable = variantsToCheck.some(variant =>
              Object.keys(tempSelection).every(key =>
                variant[key] === tempSelection[key] && variant.available_shopify
              )
            );
          }

          // Update class based on availability and impossibility
          if(!isColorOption){
            radio.disabled = !isAvailable || isImpossibleCombination;
          }
          radio.classList.toggle('option-unavailable', !isAvailable);
          radio.classList.toggle('option-impossible', isImpossibleCombination);
        });
      });
      if(this.allowDebug()){
        console.log(`Product options availability updated for ${this.elements.parentCard.product.title}`, this, this.elements.parentCard);
      }
    }

    /**
     * Captures the current selections from all option groups
     * @returns {Object} Object mapping option keys to their selected values
     */
    getCurrentSelections(){
      const selections = {};

      if(!this.elements.optionSelectContainer) return selections;

      const optionGroups = this.elements.optionSelectContainer.querySelectorAll('.product-card__option');

      optionGroups.forEach((group) => {
        const selectedRadio = group.querySelector('input:checked');
        if (selectedRadio) {
          const optionKey = group.dataset.optionKey;
          const optionValue = selectedRadio.value;
          if (optionKey && optionValue) {
            selections[optionKey] = optionValue;
            if(this.allowDebug()){
              console.log(`Captured selection: ${optionKey} = ${optionValue}`);
            }
          }
        }
      });

      if(this.allowDebug()){
        console.log(`Current selections captured:`, selections);
      }

      return selections;
    }

    /**
     * Restores previously captured selections after HTML content update
     * Only restores selections if the corresponding options still exist
     * @param {Object} selections - Object mapping option keys to values to restore
     */
    restoreSelections(selections){
      if(!selections || Object.keys(selections).length === 0) return;
      if(!this.elements.optionSelectContainer) return;

      if(this.allowDebug()){
        console.log(`Attempting to restore selections:`, selections);
      }

      const optionGroups = this.elements.optionSelectContainer.querySelectorAll('.product-card__option');

      optionGroups.forEach((group) => {
        const optionKey = group.dataset.optionKey;
        const targetValue = selections[optionKey];

        if (optionKey && targetValue) {
          // Find the radio input with the matching value
          const targetRadio = group.querySelector(`input[value="${targetValue}"]`);
          if (targetRadio) {
            targetRadio.checked = true;
            if(this.allowDebug()){
              console.log(`Restored selection: ${optionKey} = ${targetValue}`);
            }
          } else if(this.allowDebug()){
            console.log(`Could not restore selection: ${optionKey} = ${targetValue} (option not available in new HTML)`);
          }
        }
      });
    }

    /**
     * Clears all selections from all option groups
     * Also clears any cart messages since the state is being reset
     */
    clearSelections(){
      if(!this.elements.optionSelectContainer) return;

      if(this.allowDebug()){
        console.log(`Clearing all selections for ${this.elements.parentCard.product.title}`);
      }

      const optionGroups = this.elements.optionSelectContainer.querySelectorAll('.product-card__option');

      optionGroups.forEach((group) => {
        const radioInputs = group.querySelectorAll('input[type="radio"]');
        radioInputs.forEach((radio) => {
          radio.checked = false;
          if(this.allowDebug()){
            console.log(`Cleared selection: ${group.dataset.optionKey} - ${radio.value}`);
          }
        });
      });

      // Clear any cart messages as well since we're resetting the state
      this.clearCartMessage();

      if(this.allowDebug()){
        console.log(`All selections cleared for ${this.elements.parentCard.product.title}`);
      }
    }
  });
}
